import RADApplication from '@/Management/RADApplication';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import Messenger from '@/Utility/Messenger';
import StorageHelper from '@/Utility/StorageHelper';
import Utilities from '@/Utility/Utilities';
import {IClientInfoDTO, ILoginDTO, INotificationsDTO, INotificationDTO, IPrincipalDTO, ITaskInformationDTO} from "coreframetypings/dto";
import KeepAliveHandler from '@/Management/KeepAliveHandler';
import MessageBoxViewModelType from '@/ViewModels/Controls/MessageBoxViewModel';
import ComponentManager from '@/Management/ComponentManager';
import ToastService from '@/Management/ToastService';

export default class User {
    private static readonly lastLoggedInUserNameStorageKey: string = 'lastLoggedInUserName';
    private static readonly isRemainSignedInKey: string = 'isRemainSignedIn';
    public pdoId: KnockoutObservable<string | number>;
    public isSingleSignOn: KnockoutObservable<boolean>;
    public isRemainSignedIn: KnockoutObservable<boolean>;

    public username: KnockoutObservable<string>;
    public password: KnockoutObservable<string>;
    public principal: KnockoutObservable<string>;

    public principalList: KnockoutObservable<any>;

    public taskInformation: KnockoutObservable<ITaskInformationDTO>;
    public menus: KnockoutObservable<any>;

    public isLoggedIn: KnockoutObservable<boolean>;
    public firstName: KnockoutObservable<string>;
    public lastName: KnockoutObservable<string>;
    public objectIdentValue: KnockoutObservable<string>;
    public isSuperUser: KnockoutObservable<boolean>;
    public clientInfoID: KnockoutObservable<any>;
    public userSpecificData: {};
    public isAllowedConfigurePublicUserData: KnockoutObservable<boolean>;

    public additionalLoginInformation: KnockoutObservable<any>;

    private notificationWatcherFailingCounter = 0;
    private taskInformationFailingCounter = 0;
    public roles: string[];
    private userTaskInterval: number;
    private notificationInterval: number;
    private assignedPrincipalsCall: JQuery.Deferred<any>;
    private ignoreSSOUserName: boolean = false;

    private userNotificationViewModels: { [uuid: string]: MessageBoxViewModelType } = {};
    
    public needsTwoFaRegistration: KnockoutObservable<boolean>;

    constructor() {
        this.pdoId = ko.observable(-1);
        this.isSingleSignOn = ko.observable(!String.isNullOrEmpty(RADApplication.getInstance().ssoUserName));
        
        // Wenn SSO aktiv ist, wird isRemainSignedIn im LocalStorage verwaltet.
        if (this.isSingleSignOn()) {
            const isRemainStorage = this.readRemainSignedFromStorage();
            this.isRemainSignedIn = ko.observable(isRemainStorage);
            this.isRemainSignedIn.subscribe((value) => {
                const storageValue = RADApplication.getInstance().ssoUserName + '_' + value;
                StorageHelper.setGlobal(User.isRemainSignedInKey, storageValue);
            });
        } else {
            this.isRemainSignedIn = ko.observable(false);
        }

        var lastUsername = StorageHelper.getGlobal(User.lastLoggedInUserNameStorageKey);
        this.username = ko.observable(lastUsername ? lastUsername : '');

        this.password = ko.observable('');
        this.principal = ko.observable('');
        this.principalList = ko.observableArray([]);

        this.taskInformation = ko.observable();
        this.menus = ko.observable();

        this.isLoggedIn = ko.observable(false);
        this.firstName = ko.observable();
        this.lastName = ko.observable();
        this.objectIdentValue = ko.observable();
        this.isSuperUser = ko.observable(false);
        this.clientInfoID = ko.observable(0);
        this.userSpecificData = {};
        this.additionalLoginInformation = ko.observable({});
        this.isAllowedConfigurePublicUserData = ko.observable(false);
        
        this.needsTwoFaRegistration = ko.observable(false);

        let currentPrincipal = this.principal();
        let isInit = true;
        
        this.principal.subscribe((newPrincipal) => {
            // call changePrincipal only if it's an other principal
            // and skip on first subscription call
            if (this.arePrincipalsEqual(currentPrincipal, newPrincipal) || isInit) {
                isInit = false;
                currentPrincipal = newPrincipal;
                return;
            }

            currentPrincipal = newPrincipal;
            const isLoggedIn = this.isLoggedIn();

            if (isLoggedIn) {
                RADEnvironmentProxy.getInstance().changePrincipal({
                    principal: newPrincipal
                }).then((principalDto) => {
                    if (!this.arePrincipalsEqual(newPrincipal, principalDto.name)) {
                        // Wechsel hat nicht richtig funktioniert
                        this.principal(principalDto.name);
                        return;
                    }

                    RADApplication.getInstance().principal.setPrincipalInfo(principalDto);
                    RADApplication.getInstance().updateCache().then(() => {
                        Messenger.createMessageAndSend('PrincipalChangedMessage', { principal: principalDto, principalName: newPrincipal });
                    });
                }).fail(() => {
                    if (RADApplication.getInstance().principal) {
                        this.principal(RADApplication.getInstance().principal.name());
                    }
                });
            }
        });

        // Aktualisiert die Liste der verfügbaren Mandanten anhand des Benutzernamen
        ko.computed(() => {
            const un = this.username();
            const singleSignOn = this.isSingleSignOn();
            const showOap = RADApplication.getInstance().isSystemParamSetAndTrue('Login.ShowOnlyAssignedPrincipals');
            const autoselectPrincipal = RADApplication.getInstance().isSystemParamSetAndTrue('Login.AutoselectPrincipal');

            if (showOap === true || autoselectPrincipal === true) {
                ko.ignoreDependencies(() => {
                    // Wenn ignoreSSOUserName gesetzt ist, bedeutet dies, das der Server uns den Usernamen zurückgesendet hat.
                    // Da dieser sich zum SSO-Usernamen unterscheiden kann (ELVIS Usecase), kann der SSO-Username nicht mehr verwendet werden.
                    const name = singleSignOn && !this.ignoreSSOUserName ? RADApplication.getInstance().ssoUserName : un;
                    this.refreshPrincipalList(name);
                });
            }
        });

        StorageHelper.addStorageChangeEventHandler((storageEvent) => {
            if (!this.isLoggedIn())
                return;
            const app = RADApplication.getInstance();
            // Wenn ForceLogout gesetzt wurde, dann alle sofort abmelden.
            if (StorageHelper.isKeySet('ForceLogout') || StorageHelper.isGlobalKeySet('ForceLogout')) {
                RADEnvironmentProxy.getInstance().autoLogout({
                    clientInfoID: RADApplication.getInstance().user.clientInfoID()
                }).then(app.afterLogout.bind(app));
            }
        });

        // Läd die Liste der initial verfügbaren Mandanten
        const showOnlyAssignedPrincipals = RADApplication.getInstance().isSystemParamSetAndTrue('Login.ShowOnlyAssignedPrincipals');

        if (showOnlyAssignedPrincipals === false) {
            RADEnvironmentProxy.getInstance().getPrincipals().then((data) => {
                data.splice(0, 0, { name: '' } as any);

                this.principalList(this.sortPrincipals(data));

                // Wenn der aktuelle Mandant nicht in der Auswahl ist, nimm den Ersten.
                if (!(this.principal() && _.findWhere(data, { name: this.principal() })))
                    this.principal(data[0].name);
            });
        }

        if (Messenger != null) {
            Messenger.registerForMessageId('RefreshPrincipalListMessage', () => { this.refreshPrincipalList(); }, this);
        }

        this.subscribeForLogout();
    }

    private readRemainSignedFromStorage(): boolean {
        const value = StorageHelper.getGlobal(User.isRemainSignedInKey);
        if (!value)
            return false;

        const index = value.lastIndexOf('_');
        if (index === -1)
            return false;

        const userName = value.substring(0, index);
        const isRemain = value.substring(index + 1);
        return userName === RADApplication.getInstance().ssoUserName
            && isRemain
            && isRemain.toLowerCase() === 'true';
    }

    /**
     * Stops polling task information if logout is forced
     */
    private subscribeForLogout() {
        RADApplication.getInstance().isForceLogout.subscribe((isForceLogout) => {
            if (isForceLogout) {
                this.stopTaskInformation();
                this.stopNotificationWatcher();
            }
        });
    }

    /**
     * Hilfsfunktion, um unnötige Änderungen (null >-> undefined <-> '') abzufangen
     * @param oldPrincipal
     * @param newPrincipal
     */
    private arePrincipalsEqual(oldPrincipal: string, newPrincipal: string) {
        const oldPrincipalIsNull = Utilities.isNullOrUndefinedOrBlank(oldPrincipal);
        const newPrincipalIsNull = Utilities.isNullOrUndefinedOrBlank(newPrincipal);

        if (oldPrincipalIsNull && newPrincipalIsNull)
            return true;

        return oldPrincipal === newPrincipal;
    }

    /** 
     * Setzt Benutzerinformationen anhand eines LoginDTO's
     */
    public setLoginInfo(loginDto: ILoginDTO): void {
        this.setClientInfo(loginDto.clientInfo);
        this.isLoggedIn(loginDto.isAuthorized);

        // Aktualisiert die Liste der verfügbaren Mandanten
        if (loginDto.assignedPrincipals != null) {
            this.principalList(this.sortPrincipals(loginDto.assignedPrincipals));
        }
        
        this.manageTaskInformation(loginDto.taskinformation);
        this.startNotificationWatcher();

        if (loginDto.menus.userNavigationTree != null) {
            RADApplication.getInstance().onBeforeLoadNavigationTree(loginDto.menus.userNavigationTree);
        }

        if (loginDto.menus.userMenuTree != null) {
            RADApplication.getInstance().onBeforeLoadMenuTree(loginDto.menus.userMenuTree);
        }

        this.menus(loginDto.menus);

        if (loginDto.userSpecificData != null) {
            for (let key in loginDto.userSpecificData) {
                if (loginDto.userSpecificData.hasOwnProperty(key)) {
                    try {
                        const value = JSON.parse(loginDto.userSpecificData[key]);
                        this.setUserSpecificData(key, value, true);
                    } catch (e) {
                        const value = loginDto.userSpecificData[key];
                        this.setUserSpecificData(key, value, true);
                    }
                }
            }
        }

        this.principal(loginDto.clientInfo.user.principal);
        this.needsTwoFaRegistration(loginDto.clientInfo.user.needsTwoFaRegistration);
    }

    public isTwoFASelfServiceEnabled(): boolean {
        const is2FASelfServiceDisabled = this.additionalLoginInformation().Is2FASelfServiceDisabled == 'True';
        if (is2FASelfServiceDisabled)
            return false;

        const selfServiceEnabled = RADApplication.getInstance().configuration.systemProperties['2FA.Allow_User_Self_Enabled_2FA'] == 'True';
        const userHas2FA = this.additionalLoginInformation().IsTwoFA == 'True';

        return userHas2FA || selfServiceEnabled;
    }

    /** 
     * Setzt Benutzerinformationen anhand eines ClientInfoDTO's
     */
    public setClientInfo(clientInfoDto: IClientInfoDTO): void {
        this.pdoId(clientInfoDto.user.pdoId);
        this.firstName(clientInfoDto.user.firstName);
        this.lastName(clientInfoDto.user.lastName);
        this.objectIdentValue(clientInfoDto.user.objectIdentValue);
        this.isSuperUser(clientInfoDto.user.isSuperUser);
        this.additionalLoginInformation(clientInfoDto.user.additionalLoginInformations);
        this.roles = clientInfoDto.user.roles;
        this.clientInfoID(clientInfoDto.clientInfoID);

        // Von nun an wird der SSO Username nicht mehr verwendet. Dies muss nicht zurückgesetzt werden, da ein Login zum Neuladen des Objektes führt.
        this.ignoreSSOUserName = true;
        this.username(clientInfoDto.user.userName);
        this.isAllowedConfigurePublicUserData(clientInfoDto.user.isAllowedConfigurePublicUserData)

        if (!clientInfoDto.user.userName.startsWith('sys'))
            StorageHelper.setGlobal(User.lastLoggedInUserNameStorageKey, clientInfoDto.user.userName);
    }

    /** 
     * Ändert den Wert einer UserSpecificData
     */
    public setUserSpecificData(key: string, value: any, withoutUpdate: boolean = false): void {
        if (withoutUpdate) {
            this.addUserSpecificData(key, value);
        } else {
            const userSpecificData = this.addUserSpecificData(key);
            userSpecificData(value);
        }
    }

    /** 
     * Liefert das Observable einer UserSpecificData
     */
    public getUserSpecificData(key: string, defaultValue?: any): KnockoutObservable<any> {
        return this.addUserSpecificData(key, defaultValue);
    }

    /**
     * Prüft ob für ein key UserSpecificData vorhanden sind
     */
    public existUserSpecificData(key: string): boolean {
        return (key in this.userSpecificData);
    }

    /**
     * Fügt neue UserSpecificData hinzu, falls diese nicht existieren
     */
    private addUserSpecificData(key: string, defaultValue?: any): any {
        if (!this.existUserSpecificData(key)) {
            let trimmedValue = {};
            let keys = [];
            if (!Utilities.isNullOrUndefined(defaultValue) && typeof defaultValue === 'object' && !Array.isArray(defaultValue))
                keys = Object.getOwnPropertyNames(defaultValue);

            if (keys.length > 0) {
                keys.forEach(x => {
                    trimmedValue[x.trim()] = defaultValue[x];
                });
                this.userSpecificData[key] = ko.observable(trimmedValue).extend({ notify: 'always' });
            } else {
                this.userSpecificData[key] = ko.observable(defaultValue).extend({ notify: 'always' });
            }

            this.userSpecificData[key].subscribe((value) => {
                let json: string;
                if (typeof (value) === 'string' || value === null) {
                    json = value;
                }
                else {
                    json = JSON.stringify(value);
                }

                RADEnvironmentProxy.getInstance().updateUserSpecificData({
                    key: key,
                    value: json
                });
                if (value === null) {
                    delete this.userSpecificData[key];
                }
            });
        }

        return this.userSpecificData[key];
    }

    /** 
     * Ruft die aktuellen Task-Informationen ab und setzt diese im aktuellen User
     */
    private refreshTaskInformation(): void {
        RADEnvironmentProxy.getInstance().getCurrentUserTaskInfo().then((taskInformation) => {
            this.setTaskInformation(taskInformation);
            this.taskInformationFailingCounter = 0;
        }).fail(() => {
            this.taskInformationFailingCounter++;
            if (this.taskInformationFailingCounter > 5) {
                this.stopTaskInformation();
                console.log('Refreshing task-informations is stopped.');
            }
        });
    }

    /** 
     * Setzt die Task-Informationen neu, falls diese sich geändert haben
     */
    private setTaskInformation(newTaskInformation: any): void {
        const oldTaskInformation = this.taskInformation.peek();

        if (JSON.stringify(oldTaskInformation) !== JSON.stringify(newTaskInformation)) {
            this.taskInformation(newTaskInformation);
        }
    }

    /** 
     * Verwaltet die Informationen über die UserTasks
     */
    public manageTaskInformation(taskInformation: any): void {
        this.setTaskInformation(taskInformation);

        const tasksKeepAliveTime = RADApplication.getInstance().getSystemParam('Gui.TasksKeepAliveTime', parseInt);

        if (RADApplication.getInstance().isSystemParamSetAndTrue('Gui.ShowTasks')) {
            if (tasksKeepAliveTime != null) {
                const intervalTime = tasksKeepAliveTime * 1000;
                this.userTaskInterval = window.setInterval(this.refreshTaskInformation.bind(this), intervalTime);
            }

            if (Messenger != null) {
                Messenger.registerForMessageId('RefreshUserTasksMessage', this.refreshTaskInformation.bind(this), this);
                Messenger.registerForMessageId('PrincipalChangedMessage', this.refreshTaskInformation.bind(this), this);
            }
        }
    }
    
    /** 
     * Verwaltet die Informationen über die UserTasks
     */
    public stopTaskInformation(): void {
        Messenger.unregisterForMessageId('RefreshUserTasksMessage', this.refreshTaskInformation.bind(this), this);
        Messenger.unregisterForMessageId('PrincipalChangedMessage', this.refreshTaskInformation.bind(this), this);

        if (this.userTaskInterval != null) {
            window.clearInterval(this.userTaskInterval);
        }

        this.userTaskInterval = null;

        this.taskInformationFailingCounter = 0;
    }

    private startNotificationWatcher(): void {
        let time = RADApplication.getInstance().getSystemParam('Gui.NotificationTimer', parseInt);
        if (!time)
            time = 30;
        const timeInMs = time * 1000;
        this.notificationInterval = window.setInterval(this.refreshNotifications.bind(this), timeInMs);
    }

    private stopNotificationWatcher(): void {
        if (this.notificationInterval != null) {
            window.clearInterval(this.notificationInterval);
        }

        this.notificationInterval = null;
        this.notificationWatcherFailingCounter = 0;
    }

    private refreshNotifications(): void {
        RADEnvironmentProxy.getInstance().getNotifications().then((notifications : INotificationsDTO) => {
            this.notificationHandling(notifications.notifications);
            this.notificationWatcherFailingCounter = 0;
        }).fail(() => {
            this.notificationWatcherFailingCounter++;
            if (this.notificationWatcherFailingCounter > 5) {
                this.stopNotificationWatcher();
                console.log('Refreshing notification is stopped.');
            }
        });
    }

    private notificationHandling(notifications: INotificationDTO[]) {
        let hasSystemLockNotification = false;
        
        for (let notification of notifications) {
            const isSystemLock = (notification as any)._type  === 'systemLockNotificationDTO';
            if (isSystemLock) {
                hasSystemLockNotification = true;
                KeepAliveHandler.getInstance().notifySystemLock(notification);
            } else {
                this.showUserNotification(notification);
            }
        }

        if (hasSystemLockNotification === false) {
            KeepAliveHandler.getInstance().notifyNoSystemLock();
        }
    }

    private createUserNotification(message: string): MessageBoxViewModelType {
        const MessageBoxViewModel = ComponentManager.getViewModel<typeof MessageBoxViewModelType>('MessageBoxViewModel');
        const uuid = Utilities.getUuid();
        const vm = MessageBoxViewModel.createNoticeMessageBox(message, null, () => {
            this.hideUserNotification(uuid);
        });

        this.userNotificationViewModels[uuid] = vm;

        return vm;
    }

    private showUserNotification(dto: INotificationDTO) {
        if (dto.isTransient) {
            ToastService.showToastByMessage(dto.notificationMessage);
        }
        else {
            const messageBox = this.createUserNotification(dto.notificationMessage);
            RADApplication.getInstance().showMessageBox(messageBox);
        }
    }

    private hideUserNotification(uuid: string) {
        const vm = this.userNotificationViewModels[uuid];
        if (Utilities.isNullOrUndefined(vm))
            return;

        if (!vm.isDisposed) {
            vm.dispose();
        }
        delete this.userNotificationViewModels[uuid];
    }
    
    /**
     * Aktualisiert die Liste der Mandanten für einen Benutzer
     * @param forUserName Falls nicht gesetzt, wird der aktuelle Benutzter gewählt
     */
    private refreshPrincipalList(forUserName: string = this.username.peek()): void {
        let call = RADEnvironmentProxy.getInstance().getAssignedPrincipals({
            username: forUserName
        });
        this.assignedPrincipalsCall = call;
        call.then((data) => {
            // Es soll nur die aktuellste Anfrage berücksichtigt werden, damit die Reihenfolge der eingehenden Requests egal ist
            if (call != this.assignedPrincipalsCall) {
                return;
            }

            // Für den Benutzer gibt es mindestens einen Mandanten
            if (data.assignedPrincipals.length > 0) {
                this.principalList(this.sortPrincipals(data.assignedPrincipals));
                
                // Vorausgewählten Mandanten ermitteln, wenn das aktuelle Userobjekt schon einen Principal gesetzt hat
                let preselect = this.principal() ? _.findWhere(data.assignedPrincipals, { name: this.principal() }) : null

                if (preselect != null) {
                    this.principal(preselect.name);
                } else {
                    this.principal(this.principalList()[0].name);
                }
            }
            else {
                this.principalList([]);
                this.principal('');
            }
        });
    }
    /**
     * Gibt zurück, ob für den aktuellen User eine PDO_ID Spalte in den Listen angezeigt werden soll
     */
    public static currentUserShouldShowPDOID() {
        const user = RADApplication.getInstance().user
        let showPDO = false;
        if (!Utilities.isNullOrUndefined(user) && Utilities.isFunction(user.additionalLoginInformation) && user.additionalLoginInformation()["ShowPDOID"] === 'True')
            showPDO = true;

        return showPDO
    }

    /**
     *  Sortiert die Liste an Mandanten alphabetisch nach ihrem Namen
     * @param principalList
     */
    private sortPrincipals(principalList: Array<IPrincipalDTO>): Array<IPrincipalDTO> {
        return _.sortBy(principalList, (p: IPrincipalDTO) => { return p.name.toLowerCase(); });
    }
}